/**
 * Adapters — convert the canonical `TacticsLayout` (world metres) into
 * the legacy view shapes that older surfaces expect.
 *
 * These are one-way (read) adapters. Authoring always happens in the
 * canonical shape; preview / thumbnail surfaces consume a derived view.
 */
import type { TacticsLayout } from './types';

/** Pitch dimensions in world metres. Match
 *  apps/web2/src/components/tactics3d/formationProjection.ts so 2D and
 *  3D agree on the same pitch the layouts were authored against. */
export const FIELD_LENGTH_M = 105;
export const FIELD_WIDTH_M = 68;

/** Shape consumed by the static `<Pitch>` SVG component
 *  (apps/web2/src/components/Pitch.tsx). Coordinates are SVG pixels in
 *  the caller's chosen viewBox. */
export interface PitchPositions {
  home: { x: number; y: number; role?: string }[];
  away: { x: number; y: number; role?: string }[];
  ball?: { x: number; y: number };
  arrow?: {
    from: { x: number; y: number };
    to: { x: number; y: number };
    via?: { x: number; y: number };
  };
}

/** SVG viewBox dimensions to project into. The static `<Pitch>` uses
 *  360×200 by default; callers can match their layout. */
export interface PitchViewport {
  width: number;
  height: number;
  /** Inset from the edges (the SVG draws a touchline rectangle). */
  inset?: number;
}

/**
 * Project a TacticsLayout's world-metres positions onto an SVG
 * viewport's pixel space. Used by Pitch (read-only thumbnail) and by
 * any 2D-board fallback that wants to show a static preview before the
 * interactive surface boots.
 *
 * For the arrow: returns the *first bound arrow only* — the static
 * Pitch primitive expects a single arrow shape, so when the layout
 * carries multiple, we surface the first as a representative cue.
 */
export function toPitchPositions(
  layout: TacticsLayout,
  viewport: PitchViewport,
): PitchPositions {
  const inset = viewport.inset ?? 20;
  const innerW = viewport.width - inset * 2;
  const innerH = viewport.height - inset * 2;

  const projectXZ = (x: number, z: number) => {
    // World x ∈ [-W/2, +W/2] → svg-x ∈ [inset, width - inset]
    // World z ∈ [-L/2, +L/2] → svg-y. SVG y grows downward; we map
    //   negative z (away end / attacking) to small svg-y (top of image)
    //   so the resulting picture reads "attacking up the page", which
    //   matches the Pitch component's existing convention.
    const sx = inset + ((x + FIELD_WIDTH_M / 2) / FIELD_WIDTH_M) * innerW;
    const sy = inset + ((z + FIELD_LENGTH_M / 2) / FIELD_LENGTH_M) * innerH;
    return { x: sx, y: sy };
  };

  const home = layout.homeSpots.map((s) => ({ ...projectXZ(s.x, s.z), role: s.role }));
  const away = layout.awaySpots.map((s) => ({ ...projectXZ(s.x, s.z), role: s.role }));

  // First-arrow cue. Picks a 3-point representative if the curve has
  // more than two control points; otherwise simple from→to.
  let arrow: PitchPositions['arrow'] | undefined;
  const a = layout.arrows[0];
  if (a && a.points.length >= 2) {
    const from = projectXZ(a.points[0].x, a.points[0].z);
    const to = projectXZ(a.points[a.points.length - 1].x, a.points[a.points.length - 1].z);
    if (a.points.length >= 3) {
      const mid = a.points[Math.floor(a.points.length / 2)];
      arrow = { from, to, via: projectXZ(mid.x, mid.z) };
    } else {
      arrow = { from, to };
    }
  }

  return { home, away, arrow };
}
